import React, { useState } from 'react';
import { ExternalLink, Check, Download, Play, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Product Image Gallery Component with swap functionality
const ProductImageGallery = ({ product }) => {
  const [mainImage, setMainImage] = useState(product.image);
  
  // Collect all images for the gallery
  const allImages = [
    product.image,
    product.image2,
    ...(product.supportingImages || [])
  ].filter(Boolean);

  const handleImageClick = (clickedImage) => {
    setMainImage(clickedImage);
  };

  return (
    <>
      {/* Main Image */}
      <div className="h-72 bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center overflow-hidden relative group">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0D2847]/5 to-transparent"></div>
        <img 
          src={mainImage} 
          alt={product.name}
          className="w-full h-full object-contain p-6 relative z-10 transition-transform duration-500 group-hover:scale-105"
          onError={(e) => {
            e.target.src = 'https://customer-assets.emergentagent.com/job_complete-web-dev-1/artifacts/s34t1beg_image.png';
          }}
        />
        {product.warranty && (
          <div className="absolute top-4 right-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg z-20">
            {product.warranty}
          </div>
        )}
      </div>
      
      {/* Thumbnail Gallery */}
      {allImages.length > 1 && (
        <div className="bg-gray-100 px-4 py-3 border-t border-gray-200">
          <p className="text-xs text-gray-500 mb-2 font-medium">Click to view details</p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {allImages.map((img, idx) => (
              <img 
                key={idx}
                src={img} 
                alt={`${product.name} - View ${idx + 1}`}
                onClick={() => handleImageClick(img)}
                className={`w-16 h-16 object-cover rounded-lg border-2 shadow-sm flex-shrink-0 hover:scale-110 transition-all cursor-pointer ${
                  mainImage === img 
                    ? 'border-[#0D2847] ring-2 ring-[#0D2847]/30' 
                    : 'border-white hover:border-gray-300'
                }`}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

const MolecularHydrogen = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [activeVideo, setActiveVideo] = useState('HLd0ospXHsc');

  const products = [
    {
      name: 'HW600 2.0 Premium',
      subtitle: 'Premium Cellular Optimization Machine Complete Kit',
      description: 'Ideal for deeper therapeutic support, faster sessions, or sharing with family. With 600ml/min of pure hydrogen gas, perfect for consistent H₂ therapy for energy, inflammation, detox, or chronic health concerns. Recommended for professionals and small wellness businesses.',
      image: 'https://customer-assets.emergentagent.com/job_essence-health/artifacts/cblsvjlp_HW600.png',
      supportingImages: [
        'https://customer-assets.emergentagent.com/job_essence-health/artifacts/d0qufn99_HW300%20HW600%20Cannulla%20Photo.jpeg',
        'https://customer-assets.emergentagent.com/job_essence-health/artifacts/fg4h5g1k_HW300%20HW600%20Eye%20Goggles%20Photo.jpeg',
        'https://customer-assets.emergentagent.com/job_essence-health/artifacts/elztpnyq_HW300%20HW600%20Headphones%20Photo.jpeg',
        'https://customer-assets.emergentagent.com/job_essence-health/artifacts/ce0bnzd1_HW300%20HW600%20Infusion%20Rod%20Photo.jpeg'
      ],
      guideLink: 'https://drive.google.com/file/d/19C60zRucjqXr60_9DGWNB1ExYWFcyfWE/view?usp=sharing',
      warranty: '5 Year Warranty',
      specs: [
        '600ml/min stable gas production',
        '99.99% hydrogen purity',
        'Continuous 24-hour runtime',
        '8,000-hour gas production lifespan',
        '1,200-1,700 ppb H₂ water saturation',
        '-600 to -1000 mV ORP level',
        'Titanium with Platinum electrodes',
        'Advanced circulatory system',
        'Sleep mode function',
        'Beautiful visual display'
      ],
      includes: [
        'Two medical-grade antibacterial cannulas',
        'One antibacterial filter',
        'Advanced gas filtration system',
        '1 Bottle Axiom H2 SSH Oral Tablets',
        '1 Digital H₂ Meter',
        '1 Premium hydrogen water bottle',
        '1 Pair eye goggles for gas therapy',
        '1 Pair earphones for gas therapy',
        '1 Stainless steel infusion rod',
        '3 Rubber hoses with 3-way splitter',
        'Electrical cord',
        'User manual'
      ]
    },
    {
      name: 'HW300 2.0',
      subtitle: 'Cellular Optimization Machine Complete Kit',
      description: 'Best for individuals new to hydrogen therapy or wanting a simple, personal-use solution. Delivers 300ml/min of pure hydrogen gas. Great for daily wellness, light detox, and preventive care. Compact, affordable, and ideal for 1-2 daily sessions.',
      image: 'https://customer-assets.emergentagent.com/job_essence-health/artifacts/cblsvjlp_HW600.png',
      supportingImages: [
        'https://customer-assets.emergentagent.com/job_essence-health/artifacts/d0qufn99_HW300%20HW600%20Cannulla%20Photo.jpeg',
        'https://customer-assets.emergentagent.com/job_essence-health/artifacts/fg4h5g1k_HW300%20HW600%20Eye%20Goggles%20Photo.jpeg',
        'https://customer-assets.emergentagent.com/job_essence-health/artifacts/elztpnyq_HW300%20HW600%20Headphones%20Photo.jpeg',
        'https://customer-assets.emergentagent.com/job_essence-health/artifacts/ce0bnzd1_HW300%20HW600%20Infusion%20Rod%20Photo.jpeg'
      ],
      guideLink: 'https://drive.google.com/file/d/19C60zRucjqXr60_9DGWNB1ExYWFcyfWE/view?usp=sharing',
      warranty: '5 Year Warranty',
      specs: [
        '300ml/min stable gas production',
        '99.99% hydrogen purity',
        'Continuous 24-hour runtime',
        '8,000-hour gas production lifespan',
        '1,200-1,700 ppb H₂ water saturation',
        '-600 to -1000 mV ORP level',
        'Titanium with Platinum electrodes',
        'Advanced circulatory system',
        'Sleep mode function',
        'Beautiful visual display'
      ],
      includes: [
        'Two medical-grade antibacterial cannulas',
        'One antibacterial filter',
        'Advanced gas filtration system',
        '1 Bottle Axiom H2 SSH Oral Tablets',
        '1 Premium hydrogen water bottle',
        '1 Pair eye goggles for gas therapy',
        '1 Pair earphones for gas therapy',
        '1 Stainless steel infusion rod',
        '3 Rubber hoses with 3-way splitter',
        'Electrical cord',
        'User manual'
      ]
    },
    {
      name: 'Super-Saturated Hydrogen Water Bottle',
      subtitle: 'Portable Hydrogen Therapy System',
      description: 'Advanced bottle that infuses water with supersaturated H₂ in minutes. Over 9 ppm molecular hydrogen output with nano-thread compression and LLF Technology (7.83 Hz Schumann Resonance). Features reverse polarity ozone backflush and UVC sanitization for ultimate purity.',
      image: 'https://customer-assets.emergentagent.com/job_essence-health/artifacts/o4gone9s_SUpersaturated%20Water%20Bottle%20Main%20Image.jpg',
      image2: 'https://customer-assets.emergentagent.com/job_essence-health/artifacts/79u53gmn_Supersaturated%20Water%20Bottle%20Second%20Photo%20-%20Back.png',
      guideLink: 'https://drive.google.com/file/d/13I4zMkLj2DLiP5lrZy2mQ6u-I8C28QEO/view?usp=drive_link',
      warranty: '1 Year Limited Warranty',
      specs: [
        'Over 9 ppm H₂ output (20-min cycle)',
        '~2 ppm H₂ (3-min quick cycle)',
        '350 mL capacity',
        '-300 to -800 mV ORP range',
        'USA-made SPE/PEM membrane',
        'Platinum-coated titanium electrodes',
        'Nano-thread compression technology',
        'LLF Technology (7.83 Hz)',
        'Reverse polarity ozone backflush',
        'Integrated UVC sanitization',
        'USB-C rechargeable',
        'Tritan™ BPA-free cup'
      ],
      includes: [
        'Nasal cannula',
        'Carbon filter',
        'USB-C charging cable',
        'Usage guide'
      ]
    },
    {
      name: 'SSH Tablets',
      subtitle: 'Solid State Hydrogen Oral Tablets',
      description: 'Simple, effective, mess-free way to support wellness with molecular hydrogen. Swallow with water - generates hydrogen gas directly in stomach, activated by body heat. 100% natural with no harmful binders. Safe for pets. 30 individually packed tablets per bottle.',
      image: 'https://customer-assets.emergentagent.com/job_essence-health/artifacts/f59n2b5o_SSH%20Tablet%20Main%20Image%20%20Square%20Format.jpg',
      image2: 'https://customer-assets.emergentagent.com/job_essence-health/artifacts/qy3amtny_SSH%20Tablets%20White%20Ingredients%20Photo.jpg',
      supportingImages: [
        'https://customer-assets.emergentagent.com/job_essence-health/artifacts/0xrf00g4_SSH%20Tablets%20White%20Nutritional%20Facts%20Photo.jpg'
      ],
      specs: [
        'Activated by stomach heat',
        'Targeted internal H₂ delivery',
        '30 tablets per bottle',
        'Individually packed',
        'No taste, no mixing',
        '100% natural ingredients',
        'Safe for pets',
        'Portable & travel-friendly'
      ],
      ingredients: [
        'Microcrystalline Cellulose',
        'Inulin (Prebiotic Fiber)',
        'Citric Acid',
        'Sodium Bicarbonate',
        'Taurine',
        'Magnesium Oxide',
        'Vitamin B1 (Thiamine)',
        'Vitamin B2 (Riboflavin)',
        'Monk Fruit Extract'
      ],
      includes: [
        '30 individually packed tablets',
        'Usage instructions'
      ],
      suggestedUse: 'Take 1-2 tablets daily, preferably in the morning or before activity. Use consistently for 60-90 days for cumulative benefits.'
    }
  ];

  const educationalVideos = [
    {
      id: 'HLd0ospXHsc',
      title: 'How Molecular Hydrogen Works',
      description: 'Understand the science behind H₂ therapy and its cellular benefits'
    },
    {
      id: 'MWn4xy-LMF8',
      title: 'How Axiom Machines Make Hydrogen Water',
      description: 'Learn about the technology that creates hydrogen-rich water'
    },
    {
      id: 'AiytlGISHYI',
      title: 'Product Demonstration',
      description: 'Step-by-step guide to using your Axiom H₂ system'
    },
    {
      id: 'BsQ3BJzWvKc',
      title: 'Molecular Hydrogen 101',
      description: 'Complete guide to understanding the basics of H₂ therapy'
    }
  ];

  const testimonialVideos = [
    { id: 'mYxD9JAjEno', title: 'Customer Success Story 1' },
    { id: 'R9VjIOJcJ3c', title: 'Customer Success Story 2' },
    { id: 'Zjef67IKTAI', title: 'Customer Success Story 3' },
    { id: 'wrOFdcTWXCg', title: 'Quick Review 1' },
    { id: 'evJR7nEJpQo', title: 'Quick Review 2' },
    { id: 'GRXEKxzcYYI', title: 'Quick Review 3' }
  ];

  const studies = [
    {
      title: 'Molecular Hydrogen as an Antioxidant',
      summary: 'Research demonstrates that molecular hydrogen acts as a selective antioxidant, specifically targeting harmful free radicals while preserving beneficial oxidative signaling. Studies show H₂ can penetrate cell membranes and reduce oxidative stress at the cellular level.',
      link: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5731988/'
    },
    {
      title: 'Hydrogen Therapy for Inflammation',
      summary: 'Clinical trials indicate that hydrogen inhalation and consumption may help modulate inflammatory responses. Multiple studies have documented reduced inflammatory markers in subjects receiving molecular hydrogen therapy.',
      link: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC7475788/'
    },
    {
      title: 'Neuroprotective Effects of Molecular Hydrogen',
      summary: 'Research published in medical journals suggests molecular hydrogen may offer neuroprotective benefits. Studies have shown potential applications in supporting brain health and cognitive function through its antioxidant properties.',
      link: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4610055/'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      await axios.post(`${API}/inquiries`, {
        section: 'molecular-hydrogen',
        ...formData
      });
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(13, 40, 71, 0.85), rgba(13, 40, 71, 0.85)), url(https://customer-assets.emergentagent.com/job_complete-web-dev-1/artifacts/s34t1beg_image.png)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <h1 className="text-5xl font-bold mb-4">Molecular Hydrogen Therapy</h1>
          <p className="text-xl text-white/90 max-w-2xl mb-6">
            Optimize your health at the cellular level with Axiom H2's advanced molecular hydrogen technology.
          </p>
          <Button 
            onClick={() => document.getElementById('video-section').scrollIntoView({ behavior: 'smooth' })}
            size="lg"
            className="bg-white text-[#0D2847] hover:bg-white/90"
          >
            <Play className="mr-2" size={20} />
            Watch Introduction Video
          </Button>
        </div>
      </section>

      {/* Featured Video Section */}
      <section id="video-section" className="py-16 bg-gradient-to-b from-gray-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0D2847] mb-8 text-center">Learn About Molecular Hydrogen</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Video Player */}
            <div className="lg:col-span-2">
              <div className="relative w-full shadow-2xl rounded-2xl overflow-hidden transform hover:scale-105 transition-transform duration-300" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${activeVideo}`}
                  title="Molecular Hydrogen Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>

            {/* Video Playlist */}
            <div className="space-y-3">
              <h3 className="font-bold text-lg text-[#0D2847] mb-4 flex items-center">
                <span className="w-1 h-6 bg-gradient-to-b from-cyan-400 to-blue-600 mr-2 rounded-full"></span>
                Educational Videos
              </h3>
              {educationalVideos.map((video) => (
                <Card 
                  key={video.id}
                  className={`cursor-pointer transition-all duration-300 transform hover:-translate-y-1 ${
                    activeVideo === video.id 
                      ? 'ring-4 ring-[#0D2847] shadow-xl bg-gradient-to-br from-blue-50 to-cyan-50' 
                      : 'hover:shadow-lg bg-white'
                  }`}
                  onClick={() => setActiveVideo(video.id)}
                >
                  <CardHeader className="p-4">
                    <CardTitle className="text-sm flex items-center">
                      <Play size={16} className="mr-2 text-[#0D2847]" />
                      {video.title}
                    </CardTitle>
                    <CardDescription className="text-xs">{video.description}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Molecular Hydrogen */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#0D2847] mb-6">What is Molecular Hydrogen?</h2>
              <p className="text-gray-600 mb-4">
                Molecular hydrogen (H₂) is the smallest molecule in existence, allowing it to penetrate cell membranes and even cross the blood-brain barrier. This unique property enables H₂ to reach areas other antioxidants cannot.
              </p>
              <p className="text-gray-600 mb-4">
                Research suggests that molecular hydrogen acts as a selective antioxidant, specifically targeting harmful free radicals while preserving beneficial oxidative signaling in the body.
              </p>
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="flex items-start">
                  <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Cellular optimization</span>
                </div>
                <div className="flex items-start">
                  <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Antioxidant support</span>
                </div>
                <div className="flex items-start">
                  <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Energy & vitality</span>
                </div>
                <div className="flex items-start">
                  <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Recovery support</span>
                </div>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden shadow-xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_complete-web-dev-1/artifacts/s34t1beg_image.png" 
                alt="Axiom H2 Technology"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0D2847] mb-4 text-center">Axiom H2 Products</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Medical-grade molecular hydrogen systems designed for optimal cellular health and wellness.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {products.map((product, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-100 flex flex-col h-full">
                <ProductImageGallery product={product} />
                <CardHeader className="bg-gradient-to-r from-[#0D2847] to-[#1a3a5f] text-white relative overflow-hidden min-h-[180px]">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12"></div>
                  <CardTitle className="text-2xl relative z-10">{product.name}</CardTitle>
                  <CardDescription className="text-cyan-200 text-base relative z-10">{product.subtitle}</CardDescription>
                  <p className="text-white/90 mt-2 relative z-10 text-sm leading-relaxed">{product.description}</p>
                </CardHeader>
                <CardContent className="p-6 flex-grow flex flex-col">
                  <div className="mb-6 flex-grow">
                    <h4 className="font-bold text-[#0D2847] mb-3 text-lg flex items-center">
                      <span className="w-1 h-6 bg-gradient-to-b from-cyan-400 to-blue-600 mr-2 rounded-full"></span>
                      {product.ingredients ? 'Key Ingredients' : 'Key Specifications'}
                    </h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-blue-300 scrollbar-track-gray-100">
                      {(product.ingredients || product.specs).map((item, idx) => (
                        <div key={idx} className="flex items-start text-sm text-gray-700 bg-gradient-to-r from-blue-50 to-transparent p-2 rounded-lg hover:from-blue-100 transition-colors">
                          <Check className="text-green-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
                          <span className="font-medium">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mb-6">
                    <h4 className="font-bold text-[#0D2847] mb-3 text-lg flex items-center">
                      <span className="w-1 h-6 bg-gradient-to-b from-cyan-400 to-blue-600 mr-2 rounded-full"></span>
                      What's Included
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-green-300 scrollbar-track-gray-100">
                      {product.includes.map((item, idx) => (
                        <div key={idx} className="flex items-start text-sm text-gray-700 bg-gradient-to-r from-green-50 to-transparent p-2 rounded-lg hover:from-green-100 transition-colors">
                          <Check className="text-green-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {product.suggestedUse && (
                    <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg border-l-4 border-amber-400">
                      <h5 className="font-semibold text-amber-800 mb-2 text-sm">Suggested Use:</h5>
                      <p className="text-xs text-amber-900">{product.suggestedUse}</p>
                    </div>
                  )}
                  {product.guideLink && (
                    <a 
                      href={product.guideLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mt-auto"
                    >
                      <Button className="w-full bg-gradient-to-r from-[#0D2847] to-[#1a3a5f] hover:from-[#1a3a5f] hover:to-[#0D2847] text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                        <Download className="mr-2" size={16} />
                        View User Guide
                      </Button>
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              <strong>Disclaimer:</strong> These are wellness devices, not medical devices. They are not intended to diagnose, treat, cure, or prevent any disease.
            </p>
          </div>
        </div>
      </section>

      {/* Getting Started Guides */}
      <section className="py-16 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0D2847] mb-8 text-center">Getting Started Guides</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="text-center hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 bg-gradient-to-br from-white to-blue-50 border-2 border-blue-100">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-[#0D2847] to-[#1a3a5f] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Download className="text-white" size={32} />
                </div>
                <CardTitle className="text-xl">HW600 & HW300 Guide</CardTitle>
                <CardDescription>Complete setup and usage instructions for both systems</CardDescription>
              </CardHeader>
              <CardContent>
                <a 
                  href="https://drive.google.com/file/d/19C60zRucjqXr60_9DGWNB1ExYWFcyfWE/view?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="w-full bg-gradient-to-r from-[#0D2847] to-[#1a3a5f] hover:from-[#1a3a5f] hover:to-[#0D2847] shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                    <Download className="mr-2" size={16} />
                    View Complete Guide
                  </Button>
                </a>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 bg-gradient-to-br from-white to-cyan-50 border-2 border-cyan-100">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Download className="text-white" size={32} />
                </div>
                <CardTitle className="text-xl">Hydrogen Water Bottle</CardTitle>
                <CardDescription>How to use your portable hydrogen water bottle</CardDescription>
              </CardHeader>
              <CardContent>
                <a 
                  href="https://drive.google.com/file/d/13I4zMkLj2DLiP5lrZy2mQ6u-I8C28QEO/view?usp=drive_link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-blue-600 hover:to-cyan-500 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                    <Download className="mr-2" size={16} />
                    View Usage Guide
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Customer Testimonials */}
      <section className="py-16 bg-gradient-to-br from-white via-cyan-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0D2847] mb-4 text-center">Customer Success Stories</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Hear from real Axiom H2 users about their experiences with molecular hydrogen therapy.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonialVideos.map((video) => (
              <Card key={video.id} className="overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-100">
                <div className="relative w-full shadow-lg rounded-t-lg overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${video.id}`}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <CardHeader className="bg-gradient-to-r from-[#0D2847]/5 to-cyan-50">
                  <CardTitle className="text-sm font-semibold text-[#0D2847]">{video.title}</CardTitle>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Scientific Studies */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0D2847] mb-4 text-center">Scientific Research</h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Molecular hydrogen therapy is backed by extensive scientific research. Explore key studies:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {studies.map((study, index) => (
              <Card key={index} className="hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 bg-gradient-to-br from-white to-blue-50 border-2 border-blue-100">
                <CardHeader>
                  <div className="w-12 h-12 bg-gradient-to-br from-[#0D2847] to-[#1a3a5f] rounded-xl flex items-center justify-center mb-4 shadow-lg">
                    <ExternalLink className="text-white" size={24} />
                  </div>
                  <CardTitle className="text-lg text-[#0D2847]">{study.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">{study.summary}</p>
                  <a 
                    href={study.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-[#0D2847] hover:text-cyan-600 text-sm font-semibold transition-colors"
                  >
                    Read Full Study <ExternalLink size={14} className="ml-1" />
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Product Inquiry Form */}
      <section className="py-16 bg-gradient-to-br from-blue-50 via-cyan-50 to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="shadow-2xl border-2 border-blue-100 bg-gradient-to-br from-white to-blue-50">
            <CardHeader className="bg-gradient-to-r from-[#0D2847] to-[#1a3a5f] text-white rounded-t-xl relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12"></div>
              <CardTitle className="text-2xl text-center relative z-10">Interested in Axiom H2?</CardTitle>
              <CardDescription className="text-center text-cyan-200 relative z-10">
                Contact us to learn more about molecular hydrogen therapy and find the perfect system for your wellness goals.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-[#0D2847] font-semibold">Name *</Label>
                  <Input 
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="border-2 border-blue-200 focus:border-[#0D2847] transition-colors"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-[#0D2847] font-semibold">Email *</Label>
                  <Input 
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="border-2 border-blue-200 focus:border-[#0D2847] transition-colors"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[#0D2847] font-semibold">Phone *</Label>
                  <Input 
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="border-2 border-blue-200 focus:border-[#0D2847] transition-colors"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="message" className="text-[#0D2847] font-semibold">Message *</Label>
                  <Textarea 
                    id="message"
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder="Which product are you interested in? What are your wellness goals?"
                    className="border-2 border-blue-200 focus:border-[#0D2847] transition-colors"
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-[#0D2847] to-[#1a3a5f] hover:from-[#1a3a5f] hover:to-[#0D2847] shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 py-6 text-lg" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                </Button>
                {submitStatus === 'success' && (
                  <p className="text-green-600 text-center font-semibold">✓ Thank you! We'll be in touch soon.</p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-red-600 text-center font-semibold">✗ Something went wrong. Please try again.</p>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default MolecularHydrogen;
